import {
  Component,
  ComponentInterface,
  Event,
  EventEmitter,
  Fragment,
  getMode,
  h,
  Prop,
  State,
  Watch
} from '@stencil/core';
import clsx from 'clsx';
import { Link, ProfileNotifications, User } from '../../ds-contextual-navigation';
import { Labels, translate } from '../../../../utils/translations';
import { ActivityMapRegion, camelCase, emitClickTrackerEvent } from '../../../../utils/utils';
import { MessageType } from '../ds-message-strip/ds-message-strip';

export interface Profile {
  links: Array<Link> | Array<Link[]>;
  accountManagementUrl?: string;
  message?: {
    enabled: boolean;
    type?: MessageType;
    content?: string;
  };
  anonymousMenu?: Array<Link[]>;
  /** (optional) if boolean, disables/enabled auto opening profile on component load
   * If number: set's amount of milliseconds profile should be kept open. 10s by default */
  autoOpen?: boolean | number;
}

const DEFAULT_NOTIFICATIONS_LIMIT = 99;
const DEFAULT_PROFILE_AUTO_OPEN_TIMEOUT = 10000;

@Component({
  tag: 'ds-contextual-navigation-profile',
  styleUrls: {
    default: 'ds-contextual-navigation-profile.default.scss',
    light: 'ds-contextual-navigation-profile.light.scss'
  },
  shadow: true
})
export class ContextualNavigationProfile implements ComponentInterface {
  @Prop() loggedInUser?: User;
  @Prop() profile?: Profile;
  @Prop() notifications?: ProfileNotifications;
  @Prop() mobile?: boolean = false;
  @Prop({ reflect: true }) open: boolean = false;
  @State() private _hasUnreadNotifications: boolean = false;
  @Event() loginButtonClick: EventEmitter;

  @Event() logoutButtonClick: EventEmitter;
  @Event() menuOpenerClick: EventEmitter;
  @Event() cxsProfileMessageCloseClick: EventEmitter;

  private _dropdownContent: Element;

  private toggleProfile = () => {
    this.open = !this.open;
    emitClickTrackerEvent({
      componentName: ActivityMapRegion.Header,
      title: `${this.open ? 'Open' : 'Close'} Profile`,
      url: ''
    });
  };

  @Watch('notifications')
  private updateNotificationCounter() {
    if (this.notifications) {
      let notificationCount = 0;
      Object.keys(this.notifications).map(
        (key) => (notificationCount += this.notifications[key]?.count || 0)
      );
      this._hasUnreadNotifications = notificationCount > 0;
    } else {
      this._hasUnreadNotifications = false;
    }
  }

  /**
   * After closing the message strip focus next link or logout button is there is no links
   * @param newValue
   * @param oldValue
   */
  @Watch('profile')
  handleMessageClose(newValue: Profile, oldValue: Profile) {
    const isMessageClosing = !newValue.message?.enabled && oldValue.message?.enabled;
    if (isMessageClosing) {
      const dropdownLinks = this._dropdownContent.querySelectorAll(
        'a:not(ds-message-strip a), .action-button--logout'
      );
      if (dropdownLinks.length > 0) {
        const firstLink = dropdownLinks[0];
        const focusElement = firstLink.shadowRoot
          ? firstLink.shadowRoot.querySelector('button')
          : firstLink;
        if (focusElement) {
          // @ts-ignore TODO remove ignore after focusVisible draft is added to types
          focusElement.focus({ focusVisible: true });
        }
      }
    }
  }

  @Watch('profile')
  handleAutoOpen(newValue: Profile, oldValue: Profile) {
    if (newValue?.autoOpen && newValue?.autoOpen !== oldValue?.autoOpen) {
      this.setUpAutoOpen();
    }
  }

  private renderHeading(heading: Link, id: string) {
    return (
      <p class='links__heading' id={id}>
        {heading.url ? this.renderLink(heading) : heading.title}
      </p>
    );
  }

  private renderExternalLink() {
    return (
      <Fragment>
        <span class='links__external-label'>{translate(Labels['Navigation.LinkTargetBlank'])}</span>
        <ds-icon aria-hidden='true' name='ExternalLink' class='links__external-icon' />
      </Fragment>
    );
  }

  private renderLink(link: Link) {
    const notificationId = link.id ?? camelCase(link.title);
    const { count: notificationCount, limit: notificationsLimit = DEFAULT_NOTIFICATIONS_LIMIT } =
      this.notifications?.[notificationId] || {};

    return (
      <ds-link
        href={link.url}
        type='secondary'
        onClick={() =>
          emitClickTrackerEvent(
            {
              componentName: ActivityMapRegion.Header,
              title: `Profile Menu: ${link.title}`,
              url: link.url
            },
            { hasUiActions: false }
          )
        }
        target={link.externalLink ? '_blank' : null}
      >
        <span class='link-text'>
          {link.title}
          {link.externalLink ? this.renderExternalLink() : null}
        </span>
        {notificationCount > 0 && (
          <ds-badge
            class='links__notification-badge'
            type={getMode(this) === 'light' ? 'blue' : 'red'}
            content={
              notificationCount <= notificationsLimit ? notificationCount : `${notificationsLimit}+`
            }
            accessibleLabel={translate(Labels['Profile.NewNotifications'])}
          />
        )}
      </ds-link>
    );
  }

  private renderLogoutButton({ transparent = false } = {}) {
    return (
      <ds-button
        class={clsx(`action-button action-button--logout`, {
          'action-button--transparent': transparent
        })}
        design='Primary'
        accessibleName={translate(Labels['Profile.Logout'])}
        size={this.mobile ? 'Small' : 'Large'}
        data-testid={`cxs-header-logout-button${this.mobile ? '-mobile' : ''}`}
        onClick={() => {
          this.logoutButtonClick.emit();
          this.open = false;
          emitClickTrackerEvent({
            componentName: ActivityMapRegion.Header,
            title: 'Profile Menu: Logout',
            url: ''
          });
        }}
      >
        {translate(Labels['Profile.Logout'])}
      </ds-button>
    );
  }

  private renderUnauthenticatedMenu([caption]: Link[]) {
    return (
      <div class='unauthenticated-menu'>
        <p class='unauthenticated-menu__caption'>{caption.title}</p>
        <ds-button
          class='unauthenticated-menu__login-button action-button'
          design='Primary'
          accessibleName={translate(Labels['Profile.Login'])}
          size={this.mobile ? 'Small' : 'Large'}
          onClick={() => {
            this.loginButtonClick.emit();
            emitClickTrackerEvent({
              componentName: ActivityMapRegion.Header,
              title: 'Profile Menu: Login',
              url: ''
            });
          }}
        >
          {translate(Labels['Profile.Login'])}
        </ds-button>
      </div>
    );
  }

  private setUpAutoOpen() {
    let autoOpenTimeout = null;
    const clearTimeoutOn = (eventName: string) => {
      const handler = () => {
        clearTimeout(autoOpenTimeout);
        this._dropdownContent.removeEventListener(eventName, handler);
      };
      this._dropdownContent.addEventListener(eventName, handler);
    };
    if (
      !this.mobile &&
      (this.profile?.autoOpen === true || typeof this.profile?.autoOpen === 'number')
    ) {
      const timeout =
        typeof this.profile?.autoOpen === 'number'
          ? this.profile?.autoOpen
          : DEFAULT_PROFILE_AUTO_OPEN_TIMEOUT;
      this.open = true;
      autoOpenTimeout = setTimeout(() => {
        this.open = false;
      }, timeout);
      clearTimeoutOn('mouseover');
      clearTimeoutOn('focusin');
    }
  }

  componentWillUpdate(): void {
    this.updateNotificationCounter();
  }

  componentDidLoad() {
    this.setUpAutoOpen();
    window.addEventListener('beforeunload', () => (this.open = false));
    this.updateNotificationCounter();
  }

  render() {
    const mode = getMode(this);
    const hasUser = this.loggedInUser && Object.keys(this.loggedInUser).length;
    const hasAnonymousMenu = !!this.profile?.anonymousMenu;
    const links = hasAnonymousMenu ? this.profile?.anonymousMenu : this.profile?.links;
    const hasMultipleColumns = links?.some((link) => Array.isArray(link));
    return hasUser || hasAnonymousMenu ? (
      <ds-action-menu
        open={this.open}
        mobile={this.mobile}
        class={clsx(
          'navigation-profile',
          { 'navigation-profile--two-rows': hasMultipleColumns },
          { 'navigation-profile--mobile': this.mobile },
          { 'navigation-profile--anonymous': hasAnonymousMenu }
        )}
        openerLabel={translate(Labels.Profile)}
        openerTestId={`cxs-header-avatar-button${this.mobile ? '-mobile' : ''}`}
        dropdownTestId={`cxs-header-profile-menu${this.mobile ? '-mobile' : ''}`}
        onOpenerClick={() => {
          this.menuOpenerClick.emit();
          if (!this.mobile) {
            this.toggleProfile();
          }
        }}
        onClickOutside={() => {
          if (this.open && !this.mobile) {
            emitClickTrackerEvent({
              componentName: ActivityMapRegion.Header,
              title: 'Click Outside Profile',
              url: ''
            });
            this.open = false;
          }
        }}
        data-mode={mode}
      >
        {hasUser ? (
          <Fragment>
            {this._hasUnreadNotifications && <ds-notification-dot />}
            <div slot='opener-content' class='opener'>
              <img
                class='opener__avatar'
                src={this.loggedInUser.avatarUrl}
                title={this.loggedInUser.name}
                alt={translate(Labels['Profile.UserAvatar'])}
              />
            </div>
          </Fragment>
        ) : (
          <ds-icon slot='opener-content' name='Avatar' class='avatar__button-icon' size='big' />
        )}
        <div
          slot='dropdown-content'
          class={clsx('profile', { open: this.open })}
          ref={(el) => {
            this._dropdownContent = el;
          }}
        >
          {hasUser ? (
            <Fragment>
              <div class='profile__avatar'>
                <img
                  class='profile__avatar-image'
                  src={this.loggedInUser.avatarUrl}
                  title={this.loggedInUser.name}
                  alt={translate(Labels['Profile.UserAvatar'])}
                />
              </div>
              <p class='profile__name'>{this.loggedInUser.name}</p>
              <p class='profile__email'>{this.loggedInUser.email}</p>
              {this.profile?.message?.enabled && (
                <ds-message-strip
                  type={this.profile.message.type}
                  class='profile__message'
                  onCloseClicked={() => {
                    this.cxsProfileMessageCloseClick.emit();
                  }}
                >
                  <span
                    class='profile__message-content'
                    innerHTML={this.profile.message.content}
                    onClick={(e) => {
                      const el = e.target as HTMLLinkElement;
                      if (el.tagName === 'A') {
                        emitClickTrackerEvent({
                          componentName: ActivityMapRegion.Header,
                          title: el.innerText,
                          url: el.href
                        });
                      }
                    }}
                  ></span>
                </ds-message-strip>
              )}
            </Fragment>
          ) : null}
          {hasMultipleColumns ? (
            <div class='row'>
              {(links as Link[][]).map(([heading, ...links], index) => (
                <div class='column'>
                  {this.renderHeading(heading, `profile-nav-heading-${index}`)}
                  {!hasUser && index === 0 ? (
                    this.renderUnauthenticatedMenu(links)
                  ) : (
                    <nav aria-labelledby={`profile-nav-heading-${index}`}>
                      <ul class='links'>
                        {links.map((link) => (
                          <li class='links__link'>{this.renderLink(link)}</li>
                        ))}
                      </ul>
                      {index === 0 && hasUser
                        ? this.renderLogoutButton({ transparent: getMode(this) !== 'light' })
                        : null}
                    </nav>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <Fragment>
              {this.profile?.accountManagementUrl || this.profile?.links.length ? (
                <ul class='links'>
                  {this.profile?.accountManagementUrl && (
                    <li class='links__link links__link--my-account'>
                      {this.renderLink({
                        url: this.profile.accountManagementUrl,
                        title: translate(Labels['Profile.MyAccount'])
                      })}
                    </li>
                  )}
                  {this.profile?.links?.map((link) => (
                    <li class='links__link'>{this.renderLink(link)}</li>
                  ))}
                </ul>
              ) : null}
              {this.renderLogoutButton({ transparent: getMode(this) !== 'light' })}
            </Fragment>
          )}
        </div>
      </ds-action-menu>
    ) : (
      <ds-action-menu
        openerLabel={translate(Labels.Profile)}
        openerTestId='cxs-header-login-button'
        onOpenerClick={() => {
          this.loginButtonClick.emit();
          emitClickTrackerEvent({
            componentName: ActivityMapRegion.Header,
            title: 'Profile Menu: Login',
            url: ''
          });
        }}
        mobile={this.mobile}
      >
        <ds-icon slot='opener-content' name='Avatar' class='avatar__button-icon' size='big' />
      </ds-action-menu>
    );
  }
}
